import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Maximize2, Minimize2 } from "lucide-react";
// import './ChatWithAI.css';

const ChatWithAI = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [dimensions, setDimensions] = useState({ width: 400, height: 500 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isSending, setIsSending] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [previousState, setPreviousState] = useState(null);
  const chatRef = useRef(null);
  const chatBodyRef = useRef(null);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatHistory]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging && chatRef.current && !isFullscreen) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const chatRect = chatRef.current.getBoundingClientRect();

        const boundedX = Math.min(
          Math.max(newX, 0),
          windowWidth - chatRect.width
        );
        const boundedY = Math.min(
          Math.max(newY, 0),
          windowHeight - chatRect.height
        );

        setPosition({ x: boundedX, y: boundedY });
      }

      if (isResizing && chatRef.current && !isFullscreen) {
        const rect = chatRef.current.getBoundingClientRect();
        const newWidth = Math.max(300, e.clientX - rect.left);
        const newHeight = Math.max(400, e.clientY - rect.top);

        const maxWidth = window.innerWidth - rect.left;
        const maxHeight = window.innerHeight - rect.top;

        setDimensions({
          width: Math.min(newWidth, maxWidth),
          height: Math.min(newHeight, maxHeight),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, isFullscreen]);

  const handleHeaderMouseDown = (e) => {
    if (!isFullscreen && chatRef.current) {
      const rect = chatRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      setPreviousState({
        dimensions: { ...dimensions },
        position: { ...position },
      });

      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      setPosition({ x: 0, y: 0 });
    } else {
      if (previousState) {
        setDimensions(previousState.dimensions);
        setPosition(previousState.position);
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleToggleChat = () => {
    if (!isChatOpen) {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      setPosition({
        x: (windowWidth - dimensions.width) / 2,
        y: (windowHeight - dimensions.height) / 2,
      });
    }
    setIsChatOpen(!isChatOpen);
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    setIsSending(true);
    const newChat = [...chatHistory, { sender: "You", text: message }];

    try {
      const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${GEMINI_API_KEY}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contents: [{ parts: [{ text: message }] }],
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const aiResponse =
        data?.candidates?.[0]?.content?.parts?.[0]?.text ||
        "No response from AI.";

      setChatHistory([...newChat, { sender: "AI", text: aiResponse }]);
      setMessage("");
    } catch (error) {
      console.error("Error:", error);
      setChatHistory([
        ...newChat,
        { sender: "AI", text: "Error: Unable to fetch response." },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const MarkdownMessage = ({ content }) => (
    <ReactMarkdown
      components={{
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || "");
          return !inline && match ? (
            <SyntaxHighlighter
              style={tomorrow}
              language={match[1]}
              PreTag="div"
              {...props}
            >
              {String(children).replace(/\n$/, "")}
            </SyntaxHighlighter>
          ) : (
            <code className={className} {...props}>
              {children}
            </code>
          );
        },
      }}
      className="markdown-content"
    >
      {content}
    </ReactMarkdown>
  );

  return (
    <div className="chat-wrapper">
      <button className="btn2" onClick={handleToggleChat}>
        {isChatOpen ? "Close Chat" : "Chat with AI"}
      </button>

      {isChatOpen && (
        <div
          ref={chatRef}
          className={`chat-container ${isFullscreen ? "fullscreen" : ""}`}
          style={{
            width: `${dimensions.width}px`,
            height: `${dimensions.height}px`,
            transform: `translate(${position.x}px, ${position.y}px)`,
          }}
        >
          <div
            className={`chat-header ${!isFullscreen ? "draggable" : ""}`}
            onMouseDown={handleHeaderMouseDown}
          >
            <h2>study buddy 😎</h2>
            <div className="chat-btns">
              <button
                className="btn2"
                onClick={() => {
                  setChatHistory([]);
                }}
              >
                clear chat
              </button>
              <button
                className="btn2 fullscreen-btn"
                onClick={toggleFullscreen}
              >
                {isFullscreen ? (
                  <Minimize2 className="icon" />
                ) : (
                  <Maximize2 className="icon" />
                )}
              </button>
              <button
                className="btn2"
                onClick={() => {
                  setIsChatOpen(false);
                }}
              >
                ❌
              </button>
            </div>
          </div>

          <div
            ref={chatBodyRef}
            className="chat-body"
            style={{ height: `${dimensions.height - 120}px` }}
          >
            {chatHistory.map((chat, index) => (
              <div
                key={index}
                className={`chat-message ${
                  chat.sender === "You" ? "user-message" : "ai-message"
                }`}
              >
                <div className="sender">{chat.sender}:</div>
                <div className="markdown-wrapper">
                  <MarkdownMessage content={chat.text} />
                </div>
              </div>
            ))}
          </div>
          <div style={{ height: "80px" }}></div>

          <div className="chat-footer">
            <div className="input-group">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && !e.shiftKey && handleSendMessage()
                }
                placeholder="Type your message..."
                rows="2"
                className="message-input"
              />
              <button
                onClick={handleSendMessage}
                disabled={isSending}
                className="send-button"
              >
                {isSending ? "Sending..." : "Send"}
              </button>
            </div>
          </div>

          {!isFullscreen && (
            <div
              className="resize-handle"
              onMouseDown={() => setIsResizing(true)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ChatWithAI;
